import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Container,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { androidColors } from '../../theme/androidTheme';
import { cryptoIconsService } from '../../services/cryptoIconsService';
import { cryptoService } from '../../services/cryptoService';
import PageTitle from '../../components/PageTitle/PageTitle';

interface PopularCrypto {
  symbol: string;
  name: string;
  iconUrl?: string;
}

interface SearchResult {
  symbol: string;
  name: string;
  iconUrl?: string;
}

const AddCrypto: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [symbol, setSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popularCryptos, setPopularCryptos] = useState<PopularCrypto[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [allCryptos, setAllCryptos] = useState<{ [key: string]: { name: string, image_path: string } }>({});

  const getCryptoFullName = (symbol: string): string => {
    const names: { [key: string]: string } = {
      'BTC': 'Bitcoin',
      'ETH': 'Ethereum',
      'BNB': 'BNB',
      'SOL': 'Solana',
      'ADA': 'Cardano',
      'DOT': 'Polkadot',
      'MATIC': 'Polygon',
      'AVAX': 'Avalanche',
      'LINK': 'Chainlink',
      'UNI': 'Uniswap',
    };
    return names[symbol] || symbol;
  };

  const loadCryptoIcons = useCallback(async () => {
    try {
      const popularSymbols = ['BTC', 'ETH', 'BNB', 'SOL', 'ADA', 'DOT', 'MATIC', 'AVAX', 'LINK', 'UNI'];
      console.log('Chargement des icônes pour:', popularSymbols);
      
      const cryptos = await Promise.all(popularSymbols.map(async (symbol) => {
        const iconUrl = await cryptoIconsService.getCryptoIconUrl(symbol);
        console.log(`Icône pour ${symbol}:`, iconUrl);
        return {
          symbol,
          name: getCryptoFullName(symbol),
          iconUrl: iconUrl || undefined,
        };
      }));

      console.log('Cryptos chargées:', cryptos);
      setPopularCryptos(cryptos);
    } catch (err) {
      console.error('Erreur lors du chargement des icônes:', err);
      // Fallback vers les cryptos hardcodées avec noms complets
      setPopularCryptos([
        { symbol: 'BTC', name: 'Bitcoin' },
        { symbol: 'ETH', name: 'Ethereum' },
        { symbol: 'BNB', name: 'BNB' },
        { symbol: 'SOL', name: 'Solana' },
        { symbol: 'ADA', name: 'Cardano' },
        { symbol: 'DOT', name: 'Polkadot' },
        { symbol: 'MATIC', name: 'Polygon' },
        { symbol: 'AVAX', name: 'Avalanche' },
        { symbol: 'LINK', name: 'Chainlink' },
        { symbol: 'UNI', name: 'Uniswap' },
      ]);
    }
  }, []);

  const loadAllCryptos = async () => {
    try {
      const response = await fetch('https://cdn.cryptonvx.com/icons-data/index.json');
      if (response.ok) {
        const data = await response.json() as { [key: string]: { name: string, image_path: string } };
        setAllCryptos(data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des cryptos:', err);
    }
  };

  useEffect(() => {
    loadCryptoIcons();
    loadAllCryptos();
  }, [loadCryptoIcons]);

  const handleSearch = (searchTerm: string) => {
    setSymbol(searchTerm.toUpperCase());
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const searchTermUpper = searchTerm.toUpperCase();
    const results = Object.entries(allCryptos)
      .filter(([symbol, data]) => 
        symbol.includes(searchTermUpper) || 
        data.name.toUpperCase().includes(searchTermUpper)
      )
      .slice(0, 5)
      .map(([symbol, data]) => ({
        symbol,
        name: data.name,
        iconUrl: `https://cdn.cryptonvx.com/icons-data/${data.image_path}`
      }));

    setSearchResults(results);
    setShowSearchResults(true);
  };

  const selectSearchResult = (crypto: SearchResult) => {
    setSymbol(crypto.symbol);
    setSelectedSymbol(crypto.symbol);
    setShowSearchResults(false);
    setError(null);
    fetchCurrentPrice(crypto.symbol);
  };

  const fetchCurrentPrice = async (cryptoSymbol: string) => {
    if (!cryptoSymbol) return;

    setIsPriceLoading(true);
    setCurrentPrice(null);

    try {
      const priceValue = await cryptoService.getCurrentPrice(cryptoSymbol);
      if (priceValue !== null) {
        setCurrentPrice(priceValue);
        setPrice(priceValue.toFixed(2));
      } else {
        throw new Error('Prix non disponible');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération du prix:', err);
      setError(t('addCrypto.fetchPriceError'));
    } finally {
      setIsPriceLoading(false);
    }
  };

  const selectCrypto = (cryptoSymbol: string, cryptoName: string) => {
    setSelectedSymbol(cryptoSymbol);
    setSymbol(cryptoSymbol);
    setError(null);
    fetchCurrentPrice(cryptoSymbol);
  };

  const handleAddCrypto = async () => {
    setError(null);

    // Validation
    const finalSymbol = selectedSymbol || symbol.trim().toUpperCase();
    const quantityValue = parseFloat(quantity.replace(',', '.'));
    const priceValue = parseFloat(price.replace(',', '.'));

    if (!finalSymbol) {
      setError(t('addCrypto.symbolRequired'));
      return;
    }
    if (!quantity || quantityValue <= 0) {
      setError(t('addCrypto.quantityPositive'));
      return;
    }
    if (!price || priceValue <= 0) {
      setError(t('addCrypto.pricePositive'));
      return;
    }

    setIsLoading(true);

    try {
      const tokenData = localStorage.getItem('token');
      if (!tokenData) {
        throw new Error(t('common.unauthenticated'));
      }

      let token;
      try {
        const parsedToken = JSON.parse(tokenData);
        token = parsedToken.token;
      } catch (e) {
        token = tokenData; // Fallback si le token n'est pas au format JSON
      }

      if (!token) {
        throw new Error(t('common.invalidToken'));
      }

      // 1. Récupérer le portefeuille actuel
      const getResponse = await fetch(`https://api.cryptonvx.com/v1/app?jwt=${token}&type=data`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!getResponse.ok) {
        if (getResponse.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        throw new Error(t('addCrypto.fetchPortfolioError'));
      }

      const portfolioData = await getResponse.json();
      
      // Initialiser les listes
      let pairs: string[] = [];
      let quantities: number[] = [];
      let bidPrices: number[] = [];

      // Si le portefeuille existe, récupérer les données
      if (Array.isArray(portfolioData) && portfolioData.length > 0) {
        const data = portfolioData[0];
        if (data.pairs && data.quantity && data.bid_price) {
          pairs = data.pairs.map((e: any) => e.toString());
          quantities = data.quantity.map((e: any) => parseFloat(e.toString()) || 0);
          bidPrices = data.bid_price.map((e: any) => parseFloat(e.toString()) || 0);
        }
      }

      // 2. Ajouter la nouvelle crypto
      pairs.push(finalSymbol);
      quantities.push(quantityValue);
      bidPrices.push(priceValue);

      // 3. Envoyer le portefeuille mis à jour
      const updateResponse = await fetch(`https://api.cryptonvx.com/v1/app?jwt=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          pairs,
          quantity: quantities,
          bid_price: bidPrices,
        }),
      });

      // En mode no-cors, on ne peut pas lire la réponse
      // On considère que la requête a réussi si on arrive ici
      // Succès - retourner à la page portfolio
      navigate('/portfolio', { state: { message: t('addCrypto.addSuccess'), type: 'success' } });
    } catch (err) {
      console.error('Erreur lors de l\'ajout:', err);
      setError(err instanceof Error ? err.message : t('common.unknownError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const isFormValid = () => {
    const finalSymbol = selectedSymbol || symbol.trim();
    const quantityValue = parseFloat(quantity.replace(',', '.'));
    const priceValue = parseFloat(price.replace(',', '.'));
    
    return finalSymbol && quantityValue > 0 && priceValue > 0;
  };

  return (
    <Box
      sx={{
        backgroundColor: androidColors.darkBackground,
        minHeight: '100vh',
      }}
    >
      <PageTitle title={t('addCrypto.pageTitle')} />
      <AppBar
        position="static"
        sx={{
          backgroundColor: androidColors.darkBackground,
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            sx={{ color: androidColors.textColor, mr: 2 }}
            onClick={handleBack}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              color: androidColors.textColor,
              fontWeight: 'bold',
            }}
          >
            Ajouter une cryptomonnaie
          </Typography>
        </Toolbar>
      </AppBar>

      <Container sx={{ py: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: androidColors.error,
              '& .MuiAlert-icon': {
                color: androidColors.error,
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Cryptomonnaies populaires */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              color: androidColors.primaryGold,
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            {t('addCrypto.popularCryptos')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {popularCryptos.map((crypto) => {
              const isSelected = selectedSymbol === crypto.symbol;
              return (
                <Chip
                  key={crypto.symbol}
                  avatar={
                    crypto.iconUrl ? (
                      <Avatar
                        src={crypto.iconUrl}
                        sx={{ width: 20, height: 20 }}
                        onError={(e) => {
                          console.warn(`Erreur de chargement de l'icône pour ${crypto.symbol}`);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: `${androidColors.primaryGold}30`,
                          color: androidColors.primaryGold,
                          fontSize: '10px',
                        }}
                      >
                        {crypto.symbol[0]}
                      </Avatar>
                    )
                  }
                  label={`${crypto.symbol} - ${crypto.name}`}
                  onClick={() => selectCrypto(crypto.symbol, crypto.name)}
                  sx={{
                    backgroundColor: isSelected ? androidColors.primaryGold : androidColors.cardBackground,
                    color: isSelected ? androidColors.darkBackground : androidColors.textColor,
                    border: `1px solid ${isSelected ? androidColors.primaryGold : androidColors.borderColor}`,
                    '&:hover': {
                      backgroundColor: isSelected ? androidColors.darkGold : androidColors.cardBackgroundLight,
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Prix actuel */}
        {currentPrice !== null && (
          <Card
            sx={{
              backgroundColor: androidColors.cardBackground,
              border: `1px solid ${androidColors.borderColor}`,
              borderRadius: '12px',
              mb: 3,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: androidColors.success }} />
                <Typography
                  variant="body2"
                  sx={{ color: androidColors.textSecondary }}
                >
                  Prix actuel:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: androidColors.success,
                    fontWeight: 'bold',
                  }}
                >
                  {currentPrice.toFixed(2)} €
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Formulaire */}
        <Card
          sx={{
            backgroundColor: androidColors.cardBackground,
            border: `1px solid ${androidColors.borderColor}`,
            borderRadius: '12px',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label={t('addCrypto.searchLabel')}
                  value={symbol}
                  onChange={(e) => handleSearch(e.target.value)}
                  disabled={!!selectedSymbol}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: androidColors.textSecondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: androidColors.cardBackgroundLight,
                      '& fieldset': {
                        borderColor: androidColors.borderColor,
                      },
                      '&:hover fieldset': {
                        borderColor: androidColors.primaryGold,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: androidColors.primaryGold,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: androidColors.textSecondary,
                      '&.Mui-focused': {
                        color: androidColors.primaryGold,
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      color: androidColors.textColor,
                    },
                  }}
                />
                {showSearchResults && searchResults.length > 0 && (
                  <Paper
                    elevation={3}
                    sx={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: androidColors.cardBackground,
                      border: `1px solid ${androidColors.borderColor}`,
                      borderRadius: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <List sx={{ py: 0, width: '100%' }}>
                      {searchResults.map((result) => (
                        <ListItem
                          key={result.symbol}
                          component="div"
                          onClick={() => selectSearchResult(result)}
                          sx={{
                            cursor: 'pointer',
                            borderBottom: `1px solid ${androidColors.borderColor}`,
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                            '&:hover': {
                              backgroundColor: androidColors.cardBackgroundLight,
                            },
                            width: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={result.iconUrl}
                              sx={{ width: 32, height: 32 }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            >
                              {result.symbol[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={result.symbol}
                            secondary={result.name}
                            primaryTypographyProps={{
                              sx: { color: androidColors.textColor },
                            }}
                            secondaryTypographyProps={{
                              sx: { color: androidColors.textSecondary },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>

              <TextField
                label={t('addCrypto.quantity')}
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ step: 'any', min: '0' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: androidColors.cardBackgroundLight,
                    '& fieldset': {
                      borderColor: androidColors.borderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: androidColors.primaryGold,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: androidColors.primaryGold,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: androidColors.textSecondary,
                    '&.Mui-focused': {
                      color: androidColors.primaryGold,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: androidColors.textColor,
                  },
                }}
              />

              <TextField
                label={t('addCrypto.buyPrice')}
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                inputProps={{ step: 'any', min: '0' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ color: androidColors.textSecondary }}>
                        €
                      </Typography>
                      {isPriceLoading && (
                        <CircularProgress size={20} sx={{ ml: 1, color: androidColors.primaryGold }} />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: androidColors.cardBackgroundLight,
                    '& fieldset': {
                      borderColor: androidColors.borderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: androidColors.primaryGold,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: androidColors.primaryGold,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: androidColors.textSecondary,
                    '&.Mui-focused': {
                      color: androidColors.primaryGold,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: androidColors.textColor,
                  },
                }}
              />

              <Button
                variant="contained"
                onClick={handleAddCrypto}
                disabled={!isFormValid() || isLoading}
                sx={{
                  backgroundColor: androidColors.primaryGold,
                  color: androidColors.darkBackground,
                  fontWeight: 'bold',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: androidColors.darkGold,
                  },
                  '&:disabled': {
                    backgroundColor: androidColors.textMuted,
                    color: androidColors.textSecondary,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: androidColors.darkBackground }} />
                ) : (
                  t('addCrypto.submit')
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AddCrypto;